import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useDeleteDocument } from "@/features/documents/hooks/useDocumentsMutations";
import type { Document } from "@/types/document";

interface DeleteDocumentDialogProps {
  document: Document;
}

export function DeleteDocumentDialog({ document }: DeleteDocumentDialogProps) {
  const [open, setOpen] = useState(false);
  const deleteMutation = useDeleteDocument();

  async function onConfirm() {
    try {
      await deleteMutation.mutateAsync(document.id);
      setOpen(false);
    } catch {
      /* mutation surfaces the error via onError */
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
          aria-label="حذف المستند"
          title="حذف"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-right">
          <DialogTitle>حذف المستند</DialogTitle>
          <DialogDescription>
            هل أنت متأكد من حذف «{document.name}»؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={deleteMutation.isPending}
          >
            إلغاء
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={deleteMutation.isPending}
          >
            حذف
            {deleteMutation.isPending && <Loader className="mr-2" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
